import type { CsrfUtils } from '@change-corgi/core/csrf';
import type { EnvironmentUtils } from '@change-corgi/core/environment';
import type { EventTracker } from '@change-corgi/core/eventTracker';
import { createEventTracker as _createEventTracker } from '@change-corgi/core/eventTracker';
import type { Experiments } from '@change-corgi/core/experiments';
import type { Facebook } from '@change-corgi/core/facebook';
import type { HttpClient } from '@change-corgi/core/http';
import type { Optimizely } from '@change-corgi/core/optimizely';
import type { UserAgentUtils } from '@change-corgi/core/userAgent';

import webappInfo from 'src/webappInfo';

import type { Session } from 'src/shared/session';

import type { AmplitudeReplay } from 'src/app/shared/utils/amplitudeReplay';

import { createFacebookDecorator } from './facebookDecorator';

type Options = Readonly<{
	csrf: CsrfUtils;
	session: Session;
	environment: EnvironmentUtils;
	optimizely: Optimizely;
	experiments: Experiments;
	userAgent: UserAgentUtils;
	http: HttpClient;
	facebook: Facebook;
	amplitudeReplay: AmplitudeReplay;
}>;

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Navigator {
		standalone: boolean | undefined;
	}
}

export function createEventTracker({
	csrf,
	environment,
	session,
	optimizely,
	experiments,
	userAgent,
	http,
	facebook,
	amplitudeReplay,
}: Options): EventTracker {
	const {
		country: { countryCode },
		locale: { localeCode: locale },
	} = session;

	return _createEventTracker({
		environment: environment.getEnvironment(),
		webappInfo,
		sessionInfo: { loginState: session.loginState, locale, countryCode },
		commonProperties: {
			...session.trackingData,
		},
		decorators: [optimizely, experiments, userAgent, amplitudeReplay, createFacebookDecorator({ session, facebook })],
		listeners: [optimizely],
		post: http.post.bind(http),
		sendBeacon: http.sendBeacon.bind(http),
		getCsrfToken: csrf.getCsrfToken.bind(csrf),
		experimentOverrides: experiments.overrides,
		defaultLoggingEnabled: environment.getEnvironment() === 'development',
		// using sendBeacon when possible due to apparent issues with fetch.keepalive
		httpMode: 'beacon',
	}).decorateWindow();
}
